import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  TableCell,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  AutoAwesome as MatchIcon,
  Check as ConfirmIcon,
  Download as DownloadIcon,
  LinkOff as UnmatchIcon,
  Upload as ImportIcon,
  Delete as DeleteIcon,
  Send as PushIcon,
  Lightbulb as ResolveIcon,
} from '@mui/icons-material';
import { useAccessControl } from '../../../hooks/useAccessControl';
import { RESOURCES, ACTIONS } from '../../../hooks/usePermissions';
import { ledgerAPI } from '../../../services/api';
import { formatAccountingAmount, downloadCsv } from '../../../utils/formatters';
import { DataTable, useAppSnackbar, useConfirmDialog } from '../../common/TableDesign';
import LedgerListPage from '../LedgerListPage';
import LedgerFormDialog from '../LedgerFormDialog';
import { AccountPicker, BankAccountPicker } from '../LedgerPickers';
import { useLedgerLookups } from '../Journals';
import {
  CashStatusChip,
  RowActionsMenu,
  StatStrip,
  fmtDate,
  getErrorMessage,
  unwrap,
} from './CashShared';

const STATEMENT_STATUSES = ['DRAFT', 'READY', 'COMPLETED'];
const RESOLUTION_KINDS = ['BANK_CHARGE', 'INTEREST_INCOME'];

const stmtAccountLabel = (s, look) => {
  if (s?.account) return `${s.account.account_code} - ${s.account.account_name}`;
  return look.accountLabel(s?.account_id);
};

const emptyHeader = () => ({
  account_id: '', statement_date: new Date().toISOString().slice(0, 10),
  opening_balance: '0', closing_balance: '0',
});
const emptyLine = () => ({
  transaction_date: new Date().toISOString().slice(0, 10),
  description: '', reference: '', debit_amount: '', credit_amount: '',
});

// Bank statements: manual entry or CSV import, then the matching engine ties
// lines to book journal lines (dry-run previews first). Matched lines flow
// into a bank reconciliation via push-to-recon.
const StatementsTab = () => {
  const { can } = useAccessControl();
  const canManage = can(RESOURCES.LEDGER.CASH, ACTIONS.CREATE);
  const look = useLedgerLookups();
  const { showSnackbar, SnackbarComponent } = useAppSnackbar();
  const { confirm, ConfirmDialogComponent } = useConfirmDialog();

  const [refreshKey, setRefreshKey] = useState(0);
  const [showCreate, setShowCreate] = useState(false);
  const [header, setHeader] = useState(emptyHeader());
  const [saving, setSaving] = useState(false);

  const [showImport, setShowImport] = useState(false);
  const [importForm, setImportForm] = useState({ ...emptyHeader(), csv_text: '' });
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [showAddLines, setShowAddLines] = useState(false);
  const [newLine, setNewLine] = useState(emptyLine());
  const [csvAppend, setCsvAppend] = useState('');

  const [matchPlan, setMatchPlan] = useState(null);
  const [matching, setMatching] = useState(false);

  const [confirmDlg, setConfirmDlg] = useState({ open: false, line: null, entry_line_id: '' });
  const [resolveDlg, setResolveDlg] = useState({ open: false, line: null, kind: 'BANK_CHARGE', account_id: '' });

  const reload = () => setRefreshKey((k) => k + 1);

  const openDetail = async (row) => {
    setDetailLoading(true);
    try {
      const res = await ledgerAPI.getBankStatement(row.statement_id);
      const data = unwrap(res);
      setDetail(data);
      setMatchPlan(null);
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'Failed to fetch statement'), 'error');
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async (id) => {
    try {
      const res = await ledgerAPI.getBankStatement(id ?? detail.statement_id);
      setDetail(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
    reload();
  };

  const submitCreate = async () => {
    if (!header.account_id) {
      showSnackbar('Bank account is required', 'error');
      return;
    }
    try {
      setSaving(true);
      await ledgerAPI.createBankStatement({
        account_id: Number(header.account_id),
        statement_date: header.statement_date,
        opening_balance: Number(header.opening_balance) || 0,
        closing_balance: Number(header.closing_balance) || 0,
      });
      showSnackbar('Statement created as DRAFT', 'success');
      setShowCreate(false);
      setHeader(emptyHeader());
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const runPreview = async () => {
    if (!importForm.csv_text?.trim()) {
      showSnackbar('Paste CSV text first', 'error');
      return;
    }
    try {
      setPreviewing(true);
      const res = await ledgerAPI.previewBankStatementCsv({ csv_text: importForm.csv_text });
      setPreview(unwrap(res));
    } catch (error) {
      showSnackbar(getErrorMessage(error, 'CSV could not be parsed'), 'error');
      setPreview(null);
    } finally {
      setPreviewing(false);
    }
  };

  const submitImport = async () => {
    if (!importForm.account_id) {
      showSnackbar('Bank account is required', 'error');
      return;
    }
    if (!importForm.csv_text?.trim()) {
      showSnackbar('CSV text is required', 'error');
      return;
    }
    try {
      setSaving(true);
      const res = await ledgerAPI.importBankStatementCsv({
        account_id: Number(importForm.account_id),
        statement_date: importForm.statement_date,
        opening_balance: Number(importForm.opening_balance) || 0,
        closing_balance: Number(importForm.closing_balance) || 0,
        csv_text: importForm.csv_text,
      });
      const data = unwrap(res);
      showSnackbar(`Imported ${data.imported_lines ?? ''} lines`.trim(), 'success');
      setShowImport(false);
      setImportForm({ ...emptyHeader(), csv_text: '' });
      setPreview(null);
      reload();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setSaving(false);
    }
  };

  const submitAddLines = async () => {
    const payload = {};
    const hasSingle = newLine.description?.trim() && (Number(newLine.debit_amount) > 0 || Number(newLine.credit_amount) > 0);
    if (hasSingle) {
      payload.lines = [{
        transaction_date: newLine.transaction_date,
        description: newLine.description.trim(),
        ...(newLine.reference?.trim() ? { reference: newLine.reference.trim() } : {}),
        debit_amount: Number(newLine.debit_amount) || 0,
        credit_amount: Number(newLine.credit_amount) || 0,
      }];
    }
    if (csvAppend?.trim()) payload.csv_text = csvAppend;
    if (!payload.lines && !payload.csv_text) {
      showSnackbar('Add a line or paste CSV rows first', 'error');
      return;
    }
    try {
      await ledgerAPI.addBankStatementLines(detail.statement_id, payload);
      showSnackbar('Lines added', 'success');
      setShowAddLines(false);
      setNewLine(emptyLine());
      setCsvAppend('');
      refreshDetail();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const runAutoMatch = async (dryRun) => {
    try {
      setMatching(true);
      const res = await ledgerAPI.autoMatchBankStatement(detail.statement_id, { dry_run: dryRun });
      const plan = unwrap(res);
      setMatchPlan(plan);
      if (!dryRun) {
        showSnackbar(`Matched ${plan.matched?.length ?? 0} line(s)`, 'success');
        refreshDetail();
      }
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    } finally {
      setMatching(false);
    }
  };

  const submitConfirm = async () => {
    if (!confirmDlg.entry_line_id || Number(confirmDlg.entry_line_id) <= 0) {
      showSnackbar('Book journal line ID is required', 'error');
      return;
    }
    try {
      await ledgerAPI.confirmStatementLineMatch(confirmDlg.line.line_id, { entry_line_id: Number(confirmDlg.entry_line_id) });
      showSnackbar('Match confirmed', 'success');
      setConfirmDlg({ open: false, line: null, entry_line_id: '' });
      refreshDetail();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const submitResolve = async () => {
    if (!resolveDlg.account_id) {
      showSnackbar('Resolution account is required', 'error');
      return;
    }
    try {
      await ledgerAPI.resolveStatementLine(resolveDlg.line.line_id, {
        kind: resolveDlg.kind,
        account_id: Number(resolveDlg.account_id),
      });
      showSnackbar('Line resolved (draft journal raised)', 'success');
      setResolveDlg({ open: false, line: null, kind: 'BANK_CHARGE', account_id: '' });
      refreshDetail();
    } catch (error) {
      showSnackbar(getErrorMessage(error), 'error');
    }
  };

  const lines = Array.isArray(detail?.lines) ? detail.lines : [];
  const summary = detail?.summary;

  return (
    <>
      <LedgerListPage
        resource={RESOURCES.LEDGER.CASH}
        title="Bank Statements"
        subtitle="Import statements and match lines to book entries before reconciling"
        addLabel="New Statement"
        idKey="statement_id"
        searchPlaceholder="Search statement #, account..."
        emptyMessage="No bank statements found."
        refreshKey={refreshKey}
        fetchItems={(params) => ledgerAPI.getBankStatements(params)}
        onAdd={canManage ? () => { setHeader(emptyHeader()); setShowCreate(true); } : null}
        extraActions={canManage ? [{ label: 'Import CSV', icon: <ImportIcon />, onClick: () => { setImportForm({ ...emptyHeader(), csv_text: '' }); setPreview(null); setShowImport(true); } }] : []}
        filters={[{ key: 'status', label: 'Status', options: STATEMENT_STATUSES.map((s) => ({ label: s, value: s })) }]}
        columns={[
          { label: 'Statement #', render: (s) => <strong>{s.statement_number}</strong> },
          { label: 'Account', render: (s) => stmtAccountLabel(s, look) },
          { label: 'Date', render: (s) => fmtDate(s.statement_date) },
          { label: 'Closing', render: (s) => formatAccountingAmount(s.closing_balance), align: 'right' },
          { label: 'Source', render: (s) => s.source || 'MANUAL' },
          { label: 'Status', render: (s) => <CashStatusChip status={s.status} /> },
        ]}
        rowActions={(s) => [{ icon: ConfirmIcon, title: 'Open statement', color: 'default', onClick: () => openDetail(s) }]}
      />

      <LedgerFormDialog
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Bank Statement"
        saving={saving}
        onSubmit={submitCreate}
        values={header}
        onChange={(key, value) => setHeader((p) => ({ ...p, [key]: value }))}
        fields={[
          { key: 'account_id', label: 'Bank Account', type: 'picker', picker: BankAccountPicker, required: true, md: 6, helper: 'Must be a reconciliation-enabled account' },
          { key: 'statement_date', label: 'Statement Date', type: 'date', required: true, md: 6 },
          { key: 'opening_balance', label: 'Opening Balance', type: 'number', md: 6 },
          { key: 'closing_balance', label: 'Closing Balance', type: 'number', md: 6 },
        ]}
      />

      <Dialog open={showImport} onClose={() => setShowImport(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Import Statement From CSV</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ pt: 1, mb: 2 }}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <BankAccountPicker label="Bank Account" value={importForm.account_id} onChange={(id) => setImportForm((p) => ({ ...p, account_id: id }))} required />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField fullWidth size="small" type="date" label="Statement Date" value={importForm.statement_date} onChange={(e) => setImportForm((p) => ({ ...p, statement_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, md: 6, lg: 5 }}>
              <TextField fullWidth size="small" type="number" label="Opening" value={importForm.opening_balance} onChange={(e) => setImportForm((p) => ({ ...p, opening_balance: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 6, md: 6, lg: 5 }}>
              <TextField fullWidth size="small" type="number" label="Closing" value={importForm.closing_balance} onChange={(e) => setImportForm((p) => ({ ...p, closing_balance: e.target.value }))} />
            </Grid>
          </Grid>
          <TextField
            fullWidth multiline rows={8} size="small" label="CSV text (header: date,description,reference,debit,credit)"
            value={importForm.csv_text} onChange={(e) => setImportForm((p) => ({ ...p, csv_text: e.target.value }))}
            placeholder="date,description,reference,debit,credit&#10;2026-09-01,POS purchase,,12500,0"
          />
          {preview && (
            <Alert severity="info" sx={{ mt: 1 }}>
              {preview.line_count} line(s) - debits {formatAccountingAmount(preview.total_debits)}, credits {formatAccountingAmount(preview.total_credits)}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowImport(false)}>Cancel</Button>
          <Button onClick={runPreview} disabled={previewing}>{previewing ? 'Parsing...' : 'Preview'}</Button>
          <Button variant="contained" onClick={submitImport} disabled={saving}>{saving ? 'Importing...' : 'Import'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!detail || detailLoading} onClose={() => { setDetail(null); setMatchPlan(null); }} maxWidth="lg" fullWidth>
        <DialogTitle>
          {detail ? `Statement ${detail.statement_number}` : 'Loading...'}
        </DialogTitle>
        <DialogContent>
          {detailLoading || !detail ? (
            <Typography color="text.secondary">Loading...</Typography>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {stmtAccountLabel(detail, look)} - {fmtDate(detail.statement_date)}
              </Typography>
              <StatStrip
                tiles={[
                  { label: 'Opening', value: formatAccountingAmount(detail.opening_balance) },
                  { label: 'Closing', value: formatAccountingAmount(detail.closing_balance) },
                  { label: 'Unmatched', value: summary?.UNMATCHED ?? 0, color: 'warning.main' },
                  { label: 'Matched', value: summary?.MATCHED ?? 0, color: 'success.main' },
                  { label: 'Resolved', value: summary?.RESOLVED ?? 0, color: 'info.main' },
                  { label: 'Status', value: <CashStatusChip status={detail.status} /> },
                ]}
              />
              {detail.balance_warning && <Alert severity="warning" sx={{ mb: 2 }}>{detail.balance_warning}</Alert>}
              {canManage && detail.status !== 'COMPLETED' && (
                <Box display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={2}>
                  <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setShowAddLines(true)}>Add Lines</Button>
                  <Button size="small" variant="outlined" startIcon={<MatchIcon />} onClick={() => runAutoMatch(true)} disabled={matching}>
                    {matching ? 'Matching...' : 'Preview Auto-Match'}
                  </Button>
                  <Button size="small" variant="contained" startIcon={<MatchIcon />} onClick={() => runAutoMatch(false)} disabled={matching}>Run Auto-Match</Button>
                  <Button
                    size="small" variant="outlined" startIcon={<DownloadIcon />}
                    onClick={() => {
                      const headers = ['Line', 'Date', 'Description', 'Reference', 'Debit', 'Credit', 'Status'];
                      const rows = lines.map((l) => [
                        l.line_number, l.transaction_date || '', l.description || '', l.reference || '',
                        Number(l.debit_amount) || 0, Number(l.credit_amount) || 0, l.status || '',
                      ]);
                      downloadCsv(`statement-${detail.statement_number || detail.statement_id}.csv`, headers, rows);
                    }}
                    disabled={!lines.length}
                  >
                    Export CSV
                  </Button>
                  <Button
                    size="small" variant="outlined" startIcon={<PushIcon />}
                    onClick={() => confirm({
                      title: 'Push to reconciliation',
                      message: 'Push matched lines into a bank reconciliation?',
                      onConfirm: async () => {
                        try {
                          await ledgerAPI.pushStatementToRecon(detail.statement_id);
                          showSnackbar('Pushed to reconciliation', 'success');
                          refreshDetail();
                        } catch (error) {
                          showSnackbar(getErrorMessage(error), 'error');
                        }
                      },
                    })}
                  >
                    Push to Recon
                  </Button>
                  {detail.status === 'DRAFT' && (
                    <Button
                      size="small" color="error" startIcon={<DeleteIcon />}
                      onClick={() => confirm({
                        title: 'Delete statement',
                        message: `Delete DRAFT statement ${detail.statement_number}? This cannot be undone.`,
                        onConfirm: async () => {
                          try {
                            await ledgerAPI.deleteBankStatement(detail.statement_id);
                            showSnackbar('Statement deleted', 'success');
                            setDetail(null);
                            reload();
                          } catch (error) {
                            showSnackbar(getErrorMessage(error), 'error');
                          }
                        },
                      })}
                    >
                      Delete
                    </Button>
                  )}
                </Box>
              )}
              {matchPlan && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  Plan: {matchPlan.matched?.length ?? 0} auto-matched, {matchPlan.suggestions?.length ?? 0} need review, {matchPlan.still_unmatched?.length ?? 0} unmatched.
                  {(matchPlan.suggestions?.length ?? 0) > 0 && (
                    <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                      {matchPlan.suggestions.slice(0, 5).map((s) => (
                        <li key={s.line_id}>
                          <Typography variant="caption">
                            Line #{s.line_id} {s.description} - {s.candidates?.length ?? 0} candidate(s)
                            {s.resolve_hint ? ` (suggest ${s.resolve_hint.kind})` : ''}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </Alert>
              )}
              <DataTable
                columns={['#', 'Date', 'Description', 'Reference', 'Debit', 'Credit', 'Status', 'Actions']}
                data={lines}
                loading={false}
                emptyMessage="No lines yet."
                renderRow={(l) => (
                  <TableRow key={l.line_id}>
                    <TableCell>{l.line_number}</TableCell>
                    <TableCell>{fmtDate(l.transaction_date)}</TableCell>
                    <TableCell>{l.description}</TableCell>
                    <TableCell>{l.reference || '-'}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(l.debit_amount)}</TableCell>
                    <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>{formatAccountingAmount(l.credit_amount)}</TableCell>
                    <TableCell>
                      <CashStatusChip status={l.status} />
                    </TableCell>
                    <TableCell>
                      <RowActionsMenu
                        items={[
                          canManage && l.status === 'UNMATCHED' && {
                            icon: <ConfirmIcon fontSize="small" color="primary" />, label: 'Confirm book match',
                            onClick: () => setConfirmDlg({ open: true, line: l, entry_line_id: '' }),
                          },
                          canManage && l.status === 'UNMATCHED' && {
                            icon: <ResolveIcon fontSize="small" />, label: 'Resolve as charge/interest',
                            onClick: () => setResolveDlg({ open: true, line: l, kind: 'BANK_CHARGE', account_id: '' }),
                          },
                          canManage && l.status === 'UNMATCHED' && detail.status === 'DRAFT' && {
                            icon: <DeleteIcon fontSize="small" color="error" />, label: 'Delete line', danger: true, dividerBefore: true,
                            onClick: () => confirm({
                              title: 'Delete line',
                              message: `Delete unmatched line #${l.line_number}?`,
                              onConfirm: async () => {
                                try {
                                  await ledgerAPI.deleteBankStatementLine(l.line_id);
                                  showSnackbar('Line deleted', 'success');
                                  refreshDetail();
                                } catch (error) {
                                  showSnackbar(getErrorMessage(error), 'error');
                                }
                              },
                            }),
                          },
                          canManage && l.status === 'MATCHED' && {
                            icon: <UnmatchIcon fontSize="small" />, label: 'Release match',
                            onClick: () => confirm({
                              title: 'Unmatch line',
                              message: `Release line #${l.line_number} back to unmatched?`,
                              onConfirm: async () => {
                                try {
                                  await ledgerAPI.unmatchStatementLine(l.line_id);
                                  showSnackbar('Line unmatched', 'success');
                                  refreshDetail();
                                } catch (error) {
                                  showSnackbar(getErrorMessage(error), 'error');
                                }
                              },
                            }),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                )}
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDetail(null); setMatchPlan(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={showAddLines} onClose={() => setShowAddLines(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Add lines to {detail?.statement_number}</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Single line</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField fullWidth size="small" type="date" label="Date" value={newLine.transaction_date} onChange={(e) => setNewLine((p) => ({ ...p, transaction_date: e.target.value }))} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField fullWidth size="small" label="Description" value={newLine.description} onChange={(e) => setNewLine((p) => ({ ...p, description: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 5 }}>
              <TextField fullWidth size="small" label="Reference" value={newLine.reference} onChange={(e) => setNewLine((p) => ({ ...p, reference: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 6, md: 3, lg: 3 }}>
              <TextField fullWidth size="small" type="number" label="Debit" value={newLine.debit_amount} onChange={(e) => setNewLine((p) => ({ ...p, debit_amount: e.target.value }))} />
            </Grid>
            <Grid size={{ xs: 6, md: 3, lg: 3 }}>
              <TextField fullWidth size="small" type="number" label="Credit" value={newLine.credit_amount} onChange={(e) => setNewLine((p) => ({ ...p, credit_amount: e.target.value }))} />
            </Grid>
          </Grid>
          <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Or paste CSV rows</Typography>
          <TextField fullWidth multiline rows={5} size="small" label="CSV (header: date,description,reference,debit,credit)" value={csvAppend} onChange={(e) => setCsvAppend(e.target.value)} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddLines(false)}>Cancel</Button>
          <Button variant="contained" onClick={submitAddLines}>Add</Button>
        </DialogActions>
      </Dialog>

      <LedgerFormDialog
        open={confirmDlg.open}
        onClose={() => setConfirmDlg({ open: false, line: null, entry_line_id: '' })}
        title={`Confirm match - line #${confirmDlg.line?.line_number ?? ''}`}
        onSubmit={submitConfirm}
        values={confirmDlg}
        onChange={(key, value) => setConfirmDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[{ key: 'entry_line_id', label: 'Book journal line ID (same account, same signed amount)', type: 'number', required: true, md: 12, helper: 'Tip: run Preview Auto-Match first - candidates carry their entry line IDs' }]}
      />

      <LedgerFormDialog
        open={resolveDlg.open}
        onClose={() => setResolveDlg({ open: false, line: null, kind: 'BANK_CHARGE', account_id: '' })}
        title={`Resolve line #${resolveDlg.line?.line_number ?? ''}`}
        onSubmit={submitResolve}
        values={resolveDlg}
        onChange={(key, value) => setResolveDlg((p) => ({ ...p, [key]: value }))}
        maxWidth="sm"
        fields={[
          { key: 'kind', label: 'Resolution', type: 'select', required: true, md: 12, options: RESOLUTION_KINDS.map((k) => ({ label: k, value: k })) },
          { key: 'account_id', label: 'Charge/Interest Account', type: 'picker', picker: AccountPicker, required: true, md: 12, helper: 'Raises a DRAFT journal against this account' },
        ]}
      />

      {ConfirmDialogComponent}
      {SnackbarComponent}
    </>
  );
};

export default StatementsTab;
